import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { environment } from '@merchant-portal/environments/environment';
export type LogoVariant = 'normal' | 'white' | 'logomark' | 'logomark-white' | 'logo-only';
export type LogoSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export interface LogoConfig {
  variant: LogoVariant;
  size: LogoSize;
  alt?: string;
  customWidth?: string;
  customHeight?: string;
}

@Component({
  selector: 'lib-logo',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="logo-container" [ngClass]="logoClasses">
      <img
        [src]="logoSrc"
        [alt]="alt || 'Company Logo'"
        [style.width]="finalWidth"
        [style.height]="finalHeight"
        class="logo-image"
        (error)="onImageError($event)"
        (load)="onImageLoad($event)"
      />
    </div>
  `,
  styles: [
    `
      .logo-container {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .logo-image {
        display: block;
        max-width: 100%;
        height: auto;
        object-fit: contain;
      }

      /* Size variants */
      .logo-xsmall .logo-image {
        width: 40px;
        height: auto;
      }

      .logo-small .logo-image {
        width: 80px;
        height: auto;
      }

      .logo-medium .logo-image {
        width: 120px;
        height: auto;
      }

      .logo-large .logo-image {
        width: 160px;
        height: auto;
      }

      .logo-xlarge .logo-image {
        width: 200px;
        height: auto;
      }

      /* Variant styles */
      .logo-normal {
        /* Normal logo specific styles */
      }

      .logo-white {
        /* White logo specific styles */
        filter: brightness(0) invert(1);
      }

      /* Responsive behavior */
      @media (max-width: 768px) {
        .logo-large .logo-image,
        .logo-xlarge .logo-image {
          width: 120px;
        }
      }

      @media (max-width: 480px) {
        .logo-xsmall .logo-image {
          width: 32px;
        }

        .logo-medium .logo-image,
        .logo-large .logo-image,
        .logo-xlarge .logo-image {
          width: 100px;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogoComponent {
  @Input() variant: LogoVariant = 'normal';
  @Input() size: LogoSize = 'medium';
  @Input() alt?: string;
  @Input() customWidth?: string;
  @Input() customHeight?: string;
  @Input() logoUrl?: string;
  @Input() whiteLogoSrc?: string;
  @Input() normalLogoSrc?: string;
  @Input() whiteLogoMarkSrc?: string;
  @Input() normalLogoMarkSrc?: string;

  // Default logo sources - using proper asset paths
  private defaultNormalLogo = environment.isOldLogo
    ? 'assets/images/iki.png'
    : 'assets/images/logo.svg';
  private defaultWhiteLogo = environment.isOldLogo
    ? 'assets/images/iki-white.png'
    : 'assets/images/logo-white.svg';

  private defaultMarkLogo = 'assets/images/logo-mark.svg';
  private whiteMarkLogo = 'assets/images/logo-mark-white.svg';
  private defaultLogoOnly = 'assets/images/logo-only.svg';

  get logoSources() {
    return {
      normal: this.normalLogoSrc || this.defaultNormalLogo,
      white: this.whiteLogoSrc || this.defaultWhiteLogo,
      logomark: this.normalLogoSrc || this.defaultMarkLogo,
      'logomark-white': this.whiteLogoSrc || this.whiteMarkLogo,
      'logo-only': this.normalLogoSrc || this.defaultLogoOnly,
    };
  }

  get logoSrc(): string {
    // If a specific logoUrl is provided, use it
    if (this.logoUrl) {
      return this.logoUrl;
    }

    // Otherwise, use variant-specific sources
    return this.logoSources[this.variant];
  }

  get logoClasses(): string[] {
    const classes = ['logo', `logo-${this.variant}`, `logo-${this.size}`];
    return classes;
  }

  get finalWidth(): string | undefined {
    return this.customWidth;
  }

  get finalHeight(): string | undefined {
    return this.customHeight;
  }

  onImageError(event: Event): void {
    console.warn('Logo image failed to load:', this.logoSrc);
    // Optionally implement fallback logic here
  }

  onImageLoad(event: Event): void {
    // Logo loaded successfully
  }

  /**
   * Method to programmatically switch logo variant
   */
  switchVariant(newVariant: LogoVariant): void {
    this.variant = newVariant;
  }

  /**
   * Method to toggle between normal and white variants
   */
  toggleVariant(): void {
    this.variant = this.variant === 'normal' ? 'white' : 'normal';
  }
}
